import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormButtonGroup,
  FormTextarea,
} from '@sib/ui';
import {
  FeedbackFormField,
  useFormProps,
  useOnSubmit,
  useDifficultyOptions,
  MAX_MESSAGE_LENGTH,
} from './FeedbackDialog.utils';

interface FeedbackDialogProps {
  onClose: () => void;
  open?: boolean;
}

const formButtonGroupButtonProps = {
  className:
    'w-auto rounded-sm bg-transparent border-border text-xs text-primary hover:bg-secondary hover:border-secondary shadow-none',
  activeClassName: 'bg-secondary border-secondary',
};

export const FeedbackDialog = ({ onClose, open }: FeedbackDialogProps) => {
  const { t } = useTranslation();
  const formProps = useFormProps();
  const {
    formState: { isValid },
    reset,
  } = formProps;
  const onDialogClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);
  const { onSubmit, isPending: isSubmitting } = useOnSubmit({
    onSuccess: onDialogClose,
  });
  const difficultyOptions = useDifficultyOptions();

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        if (!open) return;
        onDialogClose();
      }}
    >
      <DialogContent className="max-h-[90vh] w-[727px] max-w-[95vw] overflow-auto px-6 py-7 sm:max-w-[70vw] sm:px-[50px]">
        <DialogHeader>
          <div className="flex flex-col items-center gap-7">
            <DialogTitle className="text-primary text-center text-xl leading-7 font-semibold">
              {t('components.feedbackDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-primary text-center text-sm leading-5">
              {t('components.feedbackDialog.subtitle')}
            </DialogDescription>
            <div className="self-stretch">
              <Form onSubmit={onSubmit} {...formProps}>
                <div className="flex flex-col items-stretch gap-7">
                  <FormButtonGroup
                    label={t(
                      'components.feedbackDialog.fields.gettingStartedEase'
                    )}
                    className="my-1 grid grid-cols-5 gap-2 sm:my-0"
                    name={FeedbackFormField.GettingStartedEase}
                    options={difficultyOptions}
                    buttonProps={formButtonGroupButtonProps}
                  />
                  <FormButtonGroup
                    label={t('components.feedbackDialog.fields.navigationEase')}
                    className="my-1 grid grid-cols-5 gap-2 sm:my-0"
                    name={FeedbackFormField.NavigationEase}
                    options={difficultyOptions}
                    buttonProps={formButtonGroupButtonProps}
                  />
                  <FormTextarea
                    label={t(
                      'components.feedbackDialog.fields.feedbackDescription.label'
                    )}
                    placeholder={t(
                      'components.feedbackDialog.fields.feedbackDescription.placeholder'
                    )}
                    className="h-[200px] resize-none"
                    name={FeedbackFormField.FeedbackDescription}
                    counter={{ max: MAX_MESSAGE_LENGTH }}
                  />
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      className="h-8"
                      variant="outline"
                      onClick={onDialogClose}
                      disabled={isSubmitting}
                      type="button"
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      disabled={!isValid}
                      className="h-8"
                      type="submit"
                      loading={isSubmitting}
                    >
                      {t('submit')}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
